
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Apple, Mail, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { signIn, signUp } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const { error } = await signIn(email, password);
        if (error) {
          toast({
            title: "Login Failed",
            description: error.message,
            variant: "destructive",
          });
        } else {
          toast({
            title: "Welcome back!",
            description: "You've been successfully logged in.",
          });
          navigate('/dashboard');
        }
      } else {
        const { error } = await signUp(email, password, firstName, lastName);
        if (error) {
          toast({
            title: "Sign Up Failed",
            description: error.message,
            variant: "destructive",
          });
        } else {
          toast({
            title: "Account Created",
            description: "Please check your email to verify your account.",
          });
        }
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-juv-cream-white to-juv-soft-blush">
      <Card className="w-full max-w-md bg-white/95 backdrop-blur-sm shadow-lg border border-juv-rose-clay/20">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center bg-gradient-to-br from-juv-terracotta to-juv-deep-chestnut">
            <span className="text-white text-2xl font-bold font-juv-heading">J</span>
          </div>
          <div>
            <CardTitle className="text-2xl font-bold font-juv-heading text-juv-deep-chestnut">
              Welcome to JUV
            </CardTitle>
            <p className="text-juv-deep-chestnut/70 mt-2">Self-care starts here.</p>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Auth Toggle */}
          <div className="flex rounded-lg p-1 bg-juv-soft-blush/30">
            <button
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                isLogin
                  ? 'bg-white shadow-sm text-juv-deep-chestnut'
                  : 'hover:bg-white/50 text-juv-deep-chestnut/70'
              }`}
            >
              Login
            </button>
            <button
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                !isLogin
                  ? 'bg-white shadow-sm text-juv-deep-chestnut'
                  : 'hover:bg-white/50 text-juv-deep-chestnut/70'
              }`}
            >
              Sign Up
            </button>
          </div>

          {/* Apple Sign In - Custom styled button */}
          <button 
            disabled
            style={{
              width: '100%',
              padding: '0.5rem 1rem',
              borderRadius: '0.375rem',
              border: '1px solid #C4A69D',
              backgroundColor: '#F8F2F0',
              color: '#5D2C1F',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: 'not-allowed',
              opacity: '0.6',
              transition: 'all 0.2s'
            }}
          >
            <Apple className="w-5 h-5" />
            Continue with Apple
          </button>

          <div className="relative">
            <Separator className="bg-juv-rose-clay/20" />
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-3 text-sm text-juv-deep-chestnut/70">
              or
            </span>
          </div>

          {/* Email Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName" className="text-juv-deep-chestnut font-medium">First Name</Label>
                  <Input
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="border-juv-rose-clay/30 bg-white focus:border-juv-terracotta focus:ring-juv-terracotta/20"
                    required={!isLogin}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName" className="text-juv-deep-chestnut font-medium">Last Name</Label>
                  <Input
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="border-juv-rose-clay/30 bg-white focus:border-juv-terracotta focus:ring-juv-terracotta/20"
                    required={!isLogin}
                  />
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="email" className="text-juv-deep-chestnut font-medium">Email</Label>
              <div className="relative">
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="border-juv-rose-clay/30 bg-white focus:border-juv-terracotta focus:ring-juv-terracotta/20 pl-10"
                  required
                />
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-juv-deep-chestnut/50" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-juv-deep-chestnut font-medium">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="border-juv-rose-clay/30 bg-white focus:border-juv-terracotta focus:ring-juv-terracotta/20 pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 hover:opacity-70 text-juv-deep-chestnut/50"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              disabled={loading}
              className="w-full text-white hover:opacity-90 transition-all duration-300 bg-gradient-to-r from-juv-terracotta to-juv-deep-chestnut"
            >
              {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
            </Button>
          </form>

          {isLogin && (
            <div className="text-center">
              <Link 
                to="/auth" 
                className="text-sm hover:opacity-70 transition-colors text-juv-terracotta"
              >
                Forgot your password?
              </Link>
            </div>
          )}

          <div className="text-center text-sm text-juv-deep-chestnut/70">
            {isLogin ? "Don't have an account? " : "Already have an account? "}
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="font-medium transition-colors hover:opacity-70 text-juv-terracotta"
            >
              {isLogin ? 'Join today' : 'Sign in'}
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Auth;
